/*
 * ClientService implementation
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserService } from 'app/shared/services/user.service';
import { Constants } from 'app/shared/config/constants';
import { BusinessUnit } from 'app/shared/models';

@Injectable ( {
  providedIn: 'root',
} )
export class ClientService {
  readonly url = Constants.clientsServiceUrl;
  readonly searchReportingLocationUrl = `${ Constants.clientsServiceUrl }/{clientNumber}/business-units`;
  readonly searchBusinessUnitSettingsUrl = `${ Constants.clientsServiceUrl }/{clientNumber}/user-business-unit-settings`;

  private businessUnitsSettings: any;

  constructor ( private http: HttpClient, private userService: UserService ) {
  }

  /**
   *
   * @param clientNumber
   * @param searchTerm
   * @param pageNumber
   * @param pageSize
   */
  searchReportingLocation (
    clientNumber: string,
    searchTerm: string,
    activeDate: string,
    pageNumber: number,
    pageSize: number,
    reportingLocationsOnly = false,
    parentLocationIds?: string,
  ): Observable<any> {
    let params = new HttpParams ()
      .append ( 'searchTerm', searchTerm )
      .append ( 'activeDate', activeDate )
      .append ( 'pageNumber', `${ pageNumber }` )
      .append ( 'pageSize', `${ pageSize }` )
      .append ( 'reportingLocationsOnly', `${ reportingLocationsOnly }` );

    const searchParentLocationIds = parentLocationIds || this.userService.currentClientLocationIds;
    if ( searchParentLocationIds ) {
      params = params.append ( 'parentLocationIds', searchParentLocationIds );
    }

    return this.http.get ( this.searchReportingLocationUrl.replace ( '{clientNumber}', clientNumber ), { params } );
  }

  lookupReportingLocations ( clientNumber: string, activeDate: string, parentLocationIds?: string ): Observable<any> {
    let params = new HttpParams ().append ( 'activeDate', activeDate || '' );

    const clientAccessibility = this.userService.currentClientLocationIds;
    if ( parentLocationIds ) {
      params = params.append ( 'parentLocationIds', parentLocationIds );
    } else {
      params = params.append ( 'parentLocationIds', clientAccessibility );
    }

    return this.http.get ( this.searchReportingLocationUrl.replace ( '{clientNumber}', clientNumber ) + '/reporting-locations-lookup', {
      params,
    } );
  }

  lookupUnknownReportingLocations ( clientNumber: string, activeDate: string ): Observable<any> {
    const params = new HttpParams ().append ( 'activeDate', activeDate )
      .append ( 'referenceNumber', 'UNKNOWN' );
    return this.http.get ( this.searchReportingLocationUrl.replace ( '{clientNumber}', clientNumber ) + '/reporting-location', {
      params,
    } );
  }

  getInsuredLocations ( clientNumber: string, reportingLocationId: string ): Observable<any> {
    return this.http.get ( `${ this.url }/${ clientNumber }/business-units/${ reportingLocationId }/insured-locations` );
  }

  getLocation ( clientNumber: string, reportingLocationId: string ): Observable<BusinessUnit> {
    return this.http.get<BusinessUnit> ( `${ this.url }/${ clientNumber }/business-units/${ reportingLocationId }` );
  }

  getEmployerLocations ( clientNumber: string, reportingLocationId: string ): Observable<any> {
    return this.http.get ( `${ this.url }/${ clientNumber }/business-units/${ reportingLocationId }/employer-locations` );
  }

  getBusinessUnitsSettings ( clientNumber: string ): Observable<any> {
    const url = this.searchBusinessUnitSettingsUrl.replace ( '{clientNumber}', clientNumber );
    return this.http.get ( url );
  }

  async getBUSettings () {
    const buSettings = {};
    if ( this.userService.currentClient ) {
      const settings = await this.getBusinessUnitsSettings ( this.userService.currentClient.clientNumber )
        .toPromise ();
      buSettings[ this.userService.currentClient.clientNumber ] = settings;
    }
    this.businessUnitsSettings = { ...buSettings };
    this.buSettings = buSettings;
  }

  get buSettings () {
    return this.businessUnitsSettings ||
    localStorage.getItem ( Constants.localStorageKeys.reporterBusinessUnitsSettings ) ?
      JSON.parse ( localStorage.getItem ( Constants.localStorageKeys.reporterBusinessUnitsSettings ) ) : {};
  }

  set buSettings ( buSettings ) {
    localStorage.setItem ( Constants.localStorageKeys.reporterBusinessUnitsSettings, JSON.stringify ( buSettings ) );
  }

  buTextSettingsByKey ( key: string ) {
    if ( !this.buSettings ) {
      return undefined;
    }
    const settings = this.buSettings[ this.userService.currentClient?.clientNumber ];
    if ( settings ) {
      const setting = settings.find ( s => s.key === key );
      if ( setting ) {
        return setting.value;
      }
    }
    return undefined;
  }

  buEnabledSettingsByKey ( key: string ) {
    const settings = this.buSettings[ this.userService.currentClient?.clientNumber ];
    if ( settings ) {
      const setting = settings.find ( s => s.key === key );
      if ( setting ) {
        return setting.value;
      }
    }
    return undefined;
  }

  searchClients ( searchTerm: string ): Observable<any[]> {
    const params = new HttpParams ()
      .append ( 'searchTerm', searchTerm );
    return this.http.get<any[]> ( this.url, { params } );
  }
}
